import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Card, CardBody, Row, Col } from "reactstrap";
import { useToasts } from "utils/useHotToast";

import { ReactComponent as EditIcon } from "assets/icons/edit.svg";
import ToastNotification from "components/Toast/ToastNotification";
import check from "assets/icons/check-circle.svg";
import slash from "assets/icons/slash-circle.svg";

import InfoForm from "./info/form";
import InfoView from "./info/view";
import UserView from "./user/view";
import UserForm from "./user/form";
import AddressView from "./address/view";
import AddressForm from "./address/form";

// API
import { useUpdateProfileMutation } from "services/profile/api";

const ProfileTab = () => {
  // Refs
  const infoFormRef = useRef(null);
  const addressFormRef = useRef(null);

  // Hooks
  const { addToast, removeToast } = useToasts();

  // Redux state
  const user = useSelector((state) => state?.profile?.user || null);

  // API
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();

  // State
  const [expandedCards, setExpandedCards] = useState({
    info: false,
    user: false,
    address: false,
  });

  // Handle form submission
  const handleInfoSubmit = async (values) => {
    try {
      const result = await updateProfile(values).unwrap();
      const toastId = addToast(<ToastNotification icon={check} title="บันทึกข้อมูลสำเร็จ" description="ข้อมูลส่วนตัวของคุณได้รับการอัปเดตเรียบร้อยแล้ว" onPrimaryClick={() => removeToast(toastId)} onSecondaryClick={() => removeToast(toastId)} variant="success" />, { autoDismiss: true, appearance: "success" });

      // Switch back to view mode
      setExpandedCards((prev) => ({ ...prev, info: false }));
      return result;
    } catch (error) {
      console.error("Update profile error:", error);
      const errorMessage = error?.data?.message || "ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง";
      
      // If there are specific field errors, they will be handled by Formik if we pass them back
      // But since we are using .unwrap(), the error will be caught here.
      
      const toastId = addToast(<ToastNotification icon={slash} title="เกิดข้อผิดพลาด" description={errorMessage} onSecondaryClick={() => removeToast(toastId)} variant="danger" />, { autoDismiss: false, appearance: "error" });
      throw error;
    }
  };

  const handleAddressSubmit = async (values) => {
    try {
      const result = await updateProfile({ addresses: [values] }).unwrap();
      const toastId = addToast(<ToastNotification icon={check} title="บันทึกข้อมูลสำเร็จ" description="ข้อมูลส่วนตัวของคุณได้รับการอัปเดตเรียบร้อยแล้ว" onPrimaryClick={() => removeToast(toastId)} onSecondaryClick={() => removeToast(toastId)} variant="success" />, { autoDismiss: true, appearance: "success" });

      // Switch back to view mode
      setExpandedCards((prev) => ({ ...prev, address: false }));
      return result;
    } catch (error) {
      console.error("Update address error:", error);
      const errorMessage = error?.data?.message || "ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง";
      const toastId = addToast(<ToastNotification icon={slash} title="เกิดข้อผิดพลาด" description={errorMessage} onSecondaryClick={() => removeToast(toastId)} variant="danger" />, { autoDismiss: false, appearance: "error" });
      throw error;
    }
  };

  return (
    <div className="contents-tab" data-testid="tab-contents">
      <>
        {/* Info */}
        <Card className="card-form mt-4">
          <CardBody>
            <div className="flex gap-3">
              <div className="flex-grow-1 w-full ms-3">
                <Row>
                  <Col sm={12} className="d-flex justify-content-between">
                    <h2 className="fw-semibold text-gray-700">ข้อมูลส่วนตัว</h2>
                    <button className="btn" onClick={() => setExpandedCards({ ...expandedCards, info: !expandedCards.info })}>
                      <EditIcon />
                    </button>
                  </Col>
                </Row>
                {expandedCards.info ? (
                  <InfoForm
                    //
                    data={user}
                    onSwitchView={() => setExpandedCards((prev) => ({ ...prev, info: false }))}
                    formRef={infoFormRef}
                    onSubmit={handleInfoSubmit}
                    isSubmitting={isUpdating}
                  />
                ) : (
                  <InfoView data={user} />
                )}
              </div>
            </div>
          </CardBody>
        </Card>

        {/* User */}
        <Card className="card-form mt-4">
          <CardBody>
            <div className="flex gap-3">
              <div className="flex-grow-1 w-full ms-3">
                <Row>
                  <Col sm={12} className="d-flex justify-content-between">
                    <h2 className="fw-semibold text-gray-700">ไอดีของคุณ</h2>
                    <button className="btn" onClick={() => setExpandedCards({ ...expandedCards, user: !expandedCards.user })}>
                      <EditIcon />
                    </button>
                  </Col>
                </Row>
                {expandedCards.user ? <UserForm data={user} onSwitchView={() => setExpandedCards({ ...expandedCards, user: false })} /> : <UserView data={user} />}
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Address */}
        <Card className="card-form mt-4">
          <CardBody>
            <div className="flex gap-3">
              <div className="flex-grow-1 w-full ms-3">
                <Row>
                  <Col sm={12} className="d-flex justify-content-between">
                    <h2 className="fw-semibold text-gray-700">ที่อยู่</h2>
                    <button className="btn" onClick={() => setExpandedCards({ ...expandedCards, address: !expandedCards.address })}>
                      <EditIcon />
                    </button>
                  </Col>
                </Row>
                {expandedCards.address ? (
                  <AddressForm
                    //
                    data={user}
                    onSwitchView={() => setExpandedCards({ ...expandedCards, address: false })}
                    formRef={addressFormRef}
                    onSubmit={handleAddressSubmit}
                    isSubmitting={isUpdating}
                  />
                ) : (
                  <AddressView data={user} />
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      </>
    </div>
  );
};

export default ProfileTab;
